import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Share, Plus, ChevronRight } from 'lucide-react';

/* ───────────────────────────────────────────────────────────────
   PWAInstallPrompt
   - Floating "Install App" pill (bottom-right) on every screen.
   - Hidden when running as installed PWA (standalone).
   - Hidden inside the Capacitor native shell (iOS / Android app).
   - Hidden for 7 days after a user taps "Maybe Later".
   - Android / Desktop Chrome: 1-tap install via beforeinstallprompt.
   - iOS Safari: opens an instructions modal (Share → Add to Home Screen).
   ─────────────────────────────────────────────────────────────── */

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_DAYS = 7;

const isStandalone = () =>
  (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)')?.matches) ||
  // iOS Safari proprietary flag
  // eslint-disable-next-line no-undef
  (typeof navigator !== 'undefined' && navigator.standalone === true);

const isCapacitorNative = () =>
  typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (isIOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
};

const recentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [visible, setVisible] = useState(false);
  const [platform] = useState(detectPlatform);

  // Decide whether to show the floating pill at all.
  useEffect(() => {
    if (isCapacitorNative()) return;          // already a native app
    if (isStandalone()) return;                // already installed PWA
    if (recentlyDismissed()) return;           // user said "later"
    setVisible(true);
  }, []);

  // Capture Android / Desktop Chrome's native install prompt event.
  useEffect(() => {
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setVisible(false);
      setShowModal(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstallClick = useCallback(async () => {
    // Android / Desktop Chrome — native install prompt.
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === 'accepted') {
          setVisible(false);
        }
      } catch {
        /* user cancelled — keep pill visible */
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }
    // iOS Safari (and any browser without beforeinstallprompt) — show modal.
    setShowModal(true);
  }, [deferredPrompt]);

  const handleMaybeLater = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
    setShowModal(false);
  }, []);

  if (!visible) return null;

  const ctaLabel = platform === 'ios' ? 'Add to Home' : 'Install App';

  return (
    <>
      {/* ───── Floating pill (bottom-right) ───── */}
      <motion.button
        data-testid="pwa-install-pill"
        onClick={handleInstallClick}
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        whileTap={{ scale: 0.96 }}
        whileHover={{ scale: 1.02, y: -1 }}
        transition={{ duration: 0.35, delay: 0.8 }}
        style={{
          position: 'fixed',
          right: 'max(14px, calc(env(safe-area-inset-right, 0px) + 12px))',
          bottom: 'max(14px, calc(env(safe-area-inset-bottom, 0px) + 78px))',
          zIndex: 9998,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px 10px 14px',
          borderRadius: '999px',
          border: '1px solid rgba(243,229,171,0.35)',
          background: 'linear-gradient(135deg, rgba(20,30,50,0.85) 0%, rgba(10,14,23,0.92) 100%)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          color: '#F3E5AB',
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '13px',
          fontWeight: 500,
          letterSpacing: '0.06em',
          cursor: 'pointer',
          boxShadow: '0 8px 28px rgba(0,0,0,0.55), 0 0 18px rgba(243,229,171,0.18), inset 0 1px 0 rgba(255,255,255,0.08)',
        }}
        aria-label={ctaLabel}
      >
        <Download size={14} strokeWidth={2.2} style={{ color: '#92FFF7' }} />
        <span>{ctaLabel}</span>
      </motion.button>

      {/* ───── iOS / fallback instructions modal ───── */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            data-testid="pwa-install-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              background: 'rgba(2,7,18,0.78)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              padding: '0',
            }}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 280 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: 460,
                margin: '0 12px max(12px, env(safe-area-inset-bottom)) 12px',
                padding: '24px 22px 20px',
                borderRadius: '24px 24px 18px 18px',
                background: 'linear-gradient(180deg, #0F172A 0%, #0a0e17 100%)',
                border: '1px solid rgba(148,163,184,0.18)',
                boxShadow: '0 -10px 60px rgba(0,0,0,0.6), 0 0 24px rgba(116,179,255,0.08)',
                color: '#E2E8F0',
              }}
            >
              {/* Close */}
              <button
                data-testid="pwa-install-modal-close"
                onClick={() => setShowModal(false)}
                style={{
                  position: 'absolute', top: 10, right: 10,
                  background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '999px',
                  width: 32, height: 32, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', cursor: 'pointer', color: 'rgba(226,232,240,0.7)',
                }}
                aria-label="Close"
              >
                <X size={16} />
              </button>

              {/* Title */}
              <h3 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 'clamp(20px, 5vw, 24px)',
                fontWeight: 500,
                color: '#F3E5AB',
                margin: '0 0 6px',
                letterSpacing: '0.02em',
              }}>
                Install Taraanaya
              </h3>
              <p style={{
                fontSize: 13,
                color: 'rgba(226,232,240,0.55)',
                margin: '0 0 18px',
                lineHeight: 1.5,
              }}>
                {platform === 'ios'
                  ? 'Add to your iPhone home screen — works offline, opens like a native app, no App Store needed.'
                  : 'Add to your home screen — works offline and opens like a native app.'}
              </p>

              {/* Steps */}
              {platform === 'ios' ? (
                <Steps
                  items={[
                    { icon: <Share size={18} />, text: <>Tap the <strong style={{color:'#92FFF7'}}>Share</strong> button at the bottom of Safari.</> },
                    { icon: <Plus size={18} />, text: <>Scroll and tap <strong style={{color:'#92FFF7'}}>Add to Home Screen</strong>.</> },
                    { icon: <ChevronRight size={18} />, text: <>Tap <strong style={{color:'#92FFF7'}}>Add</strong> in the top-right. Done!</> },
                  ]}
                />
              ) : (
                <Steps
                  items={[
                    { icon: <ChevronRight size={18} />, text: <>Open the <strong style={{color:'#92FFF7'}}>browser menu</strong> (⋮ top-right).</> },
                    { icon: <Plus size={18} />, text: <>Tap <strong style={{color:'#92FFF7'}}>Install app</strong> or <strong style={{color:'#92FFF7'}}>Add to Home screen</strong>.</> },
                    { icon: <Download size={18} />, text: <>Confirm <strong style={{color:'#92FFF7'}}>Install</strong>. The Taraanaya icon will appear on your home screen.</> },
                  ]}
                />
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button
                  data-testid="pwa-install-modal-later"
                  onClick={handleMaybeLater}
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'transparent',
                    border: '1px solid rgba(226,232,240,0.18)',
                    color: 'rgba(226,232,240,0.75)',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Maybe later
                </button>
                <button
                  data-testid="pwa-install-modal-got-it"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1.4,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 60%, #B8962E 100%)',
                    border: '1px solid rgba(243,229,171,0.55)',
                    color: '#0F172A',
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    cursor: 'pointer',
                    boxShadow: '0 0 20px rgba(212,175,55,0.35)',
                  }}
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Steps = ({ items }) => (
  <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
    {items.map((it, i) => (
      <li key={i} data-testid={`pwa-install-step-${i + 1}`} style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '10px 12px',
        background: 'rgba(116,179,255,0.06)',
        border: '1px solid rgba(116,179,255,0.12)',
        borderRadius: 12,
      }}>
        <span style={{
          flexShrink: 0,
          width: 28, height: 28,
          borderRadius: '50%',
          background: 'rgba(146,255,247,0.12)',
          color: '#92FFF7',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 700,
        }}>{i + 1}</span>
        <span style={{
          flexShrink: 0, color: 'rgba(146,255,247,0.85)',
          paddingTop: 4,
        }}>{it.icon}</span>
        <span style={{ fontSize: 13.5, lineHeight: 1.55, color: 'rgba(226,232,240,0.85)' }}>
          {it.text}
        </span>
      </li>
    ))}
  </ol>
);

export default PWAInstallPrompt;
